import { UserType } from '@inplay/models';
import { PropsWithChildren, useMemo, useState } from 'react';
import {
  OrganisationToggleEnum,
  UserManagementTableHeaderProps,
} from './userManagement.type';
import { AuthTokenResponse } from '../../service/service.type';
import {
  AddButtonWithDropdown,
  getSettingsMenuForUser,
  OrganisationToggleOption,
  renderActionButtons,
  renderSettingsMenuWithDropdown,
} from './userManagementHelper';
import { NestedHeader } from './venuesView/venuesViewHelper';
import OrganisationToggle from '../../component/organisationToggle/organisationToggle';
import OrganizationUnitDropDown from '../../component/organizationUnitDropdown/organizationUnitDropdown';
import Search from '../../component/search/search';
import Setting from '../../assets/setting.png';
import { validateAndDecodeAuthToken } from '../../utils/helper';
import { useSearchParams } from 'react-router-dom';
import { VenuesViewSearchParams } from './venuesView/venuesView.type';

export const UserManagementTableHeader = <AddOption extends string = string>({
  allUsers,
  selectedOrganisationToggle,
  setSelectedOrganisationToggle,
  handleAddActions,
  addMenuOptions,
  searchQuery,
  handleOnChangeSearch,
  selectedCount,
  unitTranslationKey,
  handleClose,
  handleActivate,
  handleDeactivate,
  handleDelete,
  actionVisibility,
  onSettingMenuSelect,
  selectedLabel,
  handleSelect,
  children,
  showOrganizationToggle,
}: PropsWithChildren<UserManagementTableHeaderProps<AddOption>>) => {
  const [searchParams] = useSearchParams();
  const [isOpenSettingMenu, setIsOpenSettingMenu] = useState<boolean>(false);

  const nestedLevel: number = parseInt(
    searchParams.get(VenuesViewSearchParams.NESTED_LEVEL) || '0',
  );
  const isNestedView: boolean = Boolean(nestedLevel);

  const userType: UserType | undefined = useMemo(() => {
    const validAuthToken: AuthTokenResponse | null =
      validateAndDecodeAuthToken();
    return validAuthToken?.type as UserType | undefined;
  }, []);

  const validAuthToken: AuthTokenResponse | null = validateAndDecodeAuthToken();

  return (
    <div className="flex items-center justify-between pl-10 pr-8">
      <div className="flex">
        {/* Left aligned buttons */}
        {selectedCount > 0 ? (
          // If there are selected rows, render action buttons
          renderActionButtons(
            selectedCount,
            unitTranslationKey,
            handleClose,
            handleActivate,
            handleDeactivate,
            handleDelete,
            actionVisibility,
          )
        ) : isNestedView ? (
          // If no selected rows but in nested view, render back button
          <NestedHeader />
        ) : (
          // else render add button
          <AddButtonWithDropdown
            addMenuOptions={addMenuOptions}
            handleClick={handleAddActions}
          />
        )}
      </div>

      {/* Display toggle (users view/venues view/orgs view) only for OrgAdmin/SubOrgAdmin if not nested view */}

      <div className="flex">
        {(validAuthToken?.type === UserType.SUB_ORGANIZATION_ADMIN ||
          (validAuthToken?.type === UserType.ORGANIZATION_ADMIN &&
            !isNestedView)) && (
          <div>
            <OrganisationToggle
              options={OrganisationToggleOption}
              selected={selectedOrganisationToggle}
              onSelect={(val: OrganisationToggleEnum) => {
                setSelectedOrganisationToggle(val);
              }}
            />
          </div>
        )}
        {}

        {selectedOrganisationToggle === OrganisationToggleEnum.VENUE_USERS &&
          allUsers?.length > 0 && (
            <OrganizationUnitDropDown
              selectedLabel={selectedLabel}
              onSelect={handleSelect}
              users={allUsers}
            />
          )}

        <div className="flex">
          {isNestedView && (
            <AddButtonWithDropdown
              addMenuOptions={addMenuOptions}
              handleClick={handleAddActions}
            />
          )}

          <Search
            query={searchQuery}
            onChange={handleOnChangeSearch}
            placeholder="Search"
            iconSize={13}
            iconRight={false}
            id="custom-search"
            containerClass="w-22 ml-3 mr-3"
            inputClass="text-sm"
          />
          {children}
          {!(
            userType === UserType.ORGANIZATION_ADMIN ||
            userType === UserType.SUB_ORGANIZATION_ADMIN
          ) &&
            renderSettingsMenuWithDropdown(
              Setting,
              isOpenSettingMenu,
              () => setIsOpenSettingMenu((prev) => !prev),
              getSettingsMenuForUser(userType),
              (label: string | undefined) => onSettingMenuSelect?.(label),
            )}
        </div>
      </div>
    </div>
  );
};
